// Podcast Hosting Module Types

export interface PodcastShow {
  id: string;
  brand_id: string;
  user_id: string;
  name: string;
  description?: string;
  thumbnail?: string;
  host?: string;
  co_host?: string;
  host_id?: string | null;
  co_host_id?: string | null;
  format: string;
  target_frequency: string;
  runtime_minutes: number;
  status: 'draft' | 'active' | 'on_hiatus' | 'archived';
  publish_platforms: string[];
  published_links?: Record<string, string>;
  product_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PodcastEpisode {
  id: string;
  brand_id: string;
  show_id: string;
  episode_number?: number;
  title: string;
  slug?: string;
  thumbnail?: string;
  topic?: string;
  description?: string;
  target_record_date?: string;
  target_publish_date?: string;
  actual_record_date?: string;
  actual_publish_date?: string;
  status: 'planned' | 'guest_invited' | 'guest_confirmed' | 'topic_finalized' | 'recorded' | 'editing' | 'published';
  notes?: string;
  recording_link?: string;
  invitation_recording_link?: string;
  published_links?: Record<string, string>;
  social_media_links?: Record<string, string>;
  social_media_created?: boolean;
  guest_id?: string;
  what_you_will_learn?: string;
  story_behind?: string;
  questions?: string;
  // DigComp 3.0 fields
  digcomp_competence_id?: string | null;
  digcomp_area_id?: string | null;
  digcomp_level_id?: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  show?: PodcastShow;
  guests?: PodcastEpisodeGuest[];
  guest?: PodcastGuest;
}

export interface PodcastGuest {
  id: string;
  brand_id: string;
  user_id: string;
  collaborator_id?: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  job_title?: string;
  bio?: string;
  image_url?: string;
  linkedin?: string;
  twitter_handle?: string;
  website?: string;
  expertise_topics: string[];
  previous_appearances: number;
  status: 'prospect' | 'contacted' | 'interested' | 'confirmed' | 'declined' | 'appeared' | 'promotion-ready';
  source?: string;
  notes?: string;
  enrichment_data?: Record<string, any>;
  created_at: string;
  updated_at: string;
  // Joined data
  collaborator?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface PodcastEpisodeGuest {
  id: string;
  episode_id: string;
  guest_id: string;
  role: 'main_guest' | 'co_guest' | 'panelist';
  status: 'invited' | 'confirmed' | 'declined' | 'appeared';
  invitation_sent_at?: string;
  confirmed_at?: string;
  created_at: string;
  // Joined data
  guest?: PodcastGuest;
  episode?: PodcastEpisode;
}

export interface PodcastCampaign {
  id: string;
  brand_id: string;
  user_id: string;
  show_id?: string;
  episode_id?: string;
  name: string;
  description?: string;
  campaign_type: 'invitation' | 'topic_discussion' | 'pre_recording' | 'reminder' | 'post_recording';
  status: 'draft' | 'active' | 'paused' | 'completed';
  trigger_conditions?: PodcastCampaignTriggerConditions | null;
  created_at: string;
  updated_at: string;
  // Joined data
  show?: PodcastShow;
  episode?: PodcastEpisode;
  emails?: PodcastEmail[];
}

export interface PodcastEmail {
  id: string;
  campaign_id: string;
  brand_id: string;
  sequence_order: number;
  subject: string;
  content?: string;
  delay_days: number;
  delay_hours: number;
  send_conditions: {
    stop_if_replied?: boolean;
    stop_if_opened?: boolean;
  };
  stop_conditions?: PodcastCampaignTriggerConditions | null;
  created_at: string;
  updated_at: string;
}

export interface PodcastSend {
  id: string;
  campaign_id: string;
  email_id: string;
  guest_id: string;
  episode_id?: string;
  recipient_email: string;
  status: 'pending' | 'sent' | 'delivered' | 'opened' | 'clicked' | 'replied' | 'bounced' | 'failed';
  scheduled_at?: string;
  sent_at?: string;
  opened_at?: string;
  replied_at?: string;
  error_message?: string;
  esp_message_id?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  guest?: PodcastGuest;
  email?: PodcastEmail;
  episode?: PodcastEpisode;
}

// Insert types
export type PodcastShowInsert = Omit<PodcastShow, 'id' | 'created_at' | 'updated_at'>;
export type PodcastEpisodeInsert = Omit<PodcastEpisode, 'id' | 'created_at' | 'updated_at' | 'show' | 'guests'>;
export type PodcastGuestInsert = Omit<PodcastGuest, 'id' | 'created_at' | 'updated_at' | 'collaborator'>;
export type PodcastCampaignInsert = Omit<PodcastCampaign, 'id' | 'created_at' | 'updated_at' | 'show' | 'episode' | 'emails'>;
export type PodcastEmailInsert = Omit<PodcastEmail, 'id' | 'created_at' | 'updated_at'>;

// Status options for UI
export const SHOW_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'on_hiatus', label: 'On Hiatus' },
  { value: 'archived', label: 'Archived' },
] as const;

export const EPISODE_STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned' },
  { value: 'guest_invited', label: 'Guest Invited' },
  { value: 'guest_confirmed', label: 'Guest Confirmed' },
  { value: 'topic_finalized', label: 'Topic Finalized' },
  { value: 'recorded', label: 'Recorded' },
  { value: 'editing', label: 'Editing' },
  { value: 'published', label: 'Published' },
] as const;

export const GUEST_STATUS_OPTIONS = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interested', label: 'Interested' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'declined', label: 'Declined' },
  { value: 'appeared', label: 'Appeared' },
  { value: 'promotion-ready', label: 'Promotion Ready' },
] as const;

/** Same shape for campaign enrollment triggers and per-step stop conditions. */
export interface PodcastCampaignTriggerConditions {
  guest_status?: string[];
  episode_status?: string[];
}

export const CAMPAIGN_TYPE_OPTIONS = [
  { value: 'invitation', label: 'Guest Invitation' },
  { value: 'topic_discussion', label: 'Topic Discussion' },
  { value: 'pre_recording', label: 'Pre-Recording' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'post_recording', label: 'Post-Recording' },
] as const;

export const SHOW_FORMAT_OPTIONS = [
  { value: 'interview', label: 'Interview' },
  { value: 'panel', label: 'Panel Discussion' },
  { value: 'solo', label: 'Solo' },
  { value: 'co-hosted', label: 'Co-Hosted' },
  { value: 'narrative', label: 'Narrative' },
  { value: 'educational', label: 'Educational' },
] as const;

export const SHOW_FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'irregular', label: 'Irregular' },
] as const;

// Podcast shows URL settings (site_url + url_prefixes for episodes list/single links)
export interface PodcastUrlPrefixes {
  episodes?: string;
  episode?: string;
}

export interface PodcastShowsSettings {
  id: string;
  brand_id: string;
  user_id?: string | null;
  site_url: string | null;
  url_prefixes: PodcastUrlPrefixes | null;
  created_at: string;
  updated_at: string;
}
